import { z } from 'zod';
import { characterIllustrationNames } from '../../elements/illustrations/names';
import { illustrationTintNames } from '../../elements/illustrations/tints';
export const CharacterIllustrationSize = z.enum(['S', 'SM', 'M', 'L', 'Full']);
export type CharacterIllustrationSize = z.infer<typeof CharacterIllustrationSize>;

/**
 * How the artwork is rendered. A flat enum rather than a union of a literal and
 * a nested enum so `catalog.jsonSchema()` serialises it as one enum instead of
 * an `anyOf` (ADR-011 Rule 5); the inferred type is still exactly
 * `'mono' | IllustrationTint`.
 */
export const CharacterIllustrationAppearance = z.enum(['mono', ...illustrationTintNames]);
export type CharacterIllustrationAppearance = z.infer<typeof CharacterIllustrationAppearance>;

export const characterIllustrationPropsSchema = z.object({
  /** Name of the illustration from the illustrations element registry */
  name: z.enum(characterIllustrationNames),
  /**
   * How the artwork is rendered. Omit for the original colour artwork; 'mono'
   * for the black-and-white line-art; a tint token name for the colour artwork
   * with its declared accent recoloured. Absence is a meaningful third state,
   * so this prop has no default. Tinting applies on native only; web cannot
   * reach inside its <img>, so it renders the untinted artwork and warns.
   */
  appearance: CharacterIllustrationAppearance.optional(),
  /**
   * Colour rendering when true; mono line-art when false.
   *
   * @deprecated Superseded by `appearance` (`false` → `'mono'`, `true` → omit).
   * Two props on one visual axis is the shape ADR-020 Rule 1 exists to prevent,
   * so this alias is time-bounded — see `characterIllustrationDeprecations` for
   * the scheduled removal. When both are supplied `appearance` wins and this is
   * ignored.
   */
  colour: z.boolean().optional(),
  /** Rendered width: S (100px) / SM (150px) / M (200px) / L (300px) / Full (fills parent width) */
  size: CharacterIllustrationSize.default('M'),
  /** Accessibility label; when omitted the illustration is decorative and hidden */
  accessibilityLabel: z.string().optional(),
});

/**
 * Props kept only for backwards compatibility, each with the change that ends
 * it.
 *
 * Data rather than a note in the prop's docstring, for two reasons: a removal
 * recorded only in prose has no schedule anything can check, and ticket handles
 * do not belong in source comments (they rot the moment the file moves). As a
 * value it is assertable — a test can hold the alias to having a named end,
 * which is the whole reason an ADR-020 Rule 1 exception was acceptable here.
 */
export const characterIllustrationDeprecations = [
  {
    prop: 'colour',
    supersededBy: 'appearance',
    removalTicket: 'GAL-1790',
    removalIssue: 'https://github.com/quitgenius/galapagos/issues/2694',
  },
] as const;

/** Parsed output type — all defaults resolved. Used by catalog/registry. */
export type CharacterIllustrationProps = z.infer<typeof characterIllustrationPropsSchema>;

/** Input type — defaulted fields are optional. Used by component signatures. */
export type CharacterIllustrationInputProps = z.input<typeof characterIllustrationPropsSchema>;
